import { useState } from 'react'
import { apiAgentChangePassword, ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertCircle, Check } from 'lucide-react'
import { useT } from '@/contexts/language'

/**
 * Change the signed-in account's own password. Shared by the agent and admin
 * consoles — the endpoint works off the JWT, so it serves both roles.
 */
export function ChangePasswordForm() {
  const t = useT()
  const [current, setCurrent] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    if (newPw.length < 8) { setError(t.agentPassword.tooShort); return }
    if (newPw !== confirm) { setError(t.agentPassword.mismatch); return }
    setLoading(true)
    try {
      await apiAgentChangePassword(current, newPw)
      setSuccess(true)
      setCurrent(''); setNewPw(''); setConfirm('')
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 400) {
        setError(t.agentPassword.wrong)
      } else {
        setError(err instanceof ApiRequestError ? err.message : t.common.error)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] mt-4">
      <div className="px-4 py-2.5 bg-secondary">
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
          {t.agentPassword.title}
        </span>
      </div>
      <div className="p-4">
        {error && (
          <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card mb-3" role="alert">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{error}</p>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-2 border border-success/40 rounded-[var(--radius-sm)] bg-card mb-3">
            <Check className="h-3.5 w-3.5 text-success shrink-0" />
            <p className="font-mono text-xs text-success">{t.agentPassword.success}</p>
          </div>
        )}
        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.agentPassword.current}
            </Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className="font-mono text-sm h-9" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.agentPassword.newPassword}
            </Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} className="font-mono text-sm h-9" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.agentPassword.confirm}
            </Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="font-mono text-sm h-9" />
          </div>
          <div className="flex justify-end pt-1">
            <Button type="submit" size="sm" className="font-mono text-xs h-9" disabled={loading || !current || !newPw || !confirm}>
              {loading ? t.agentPassword.changing : t.agentPassword.change}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
